import { useEffect, useMemo, useState } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import FacilityCompanyEmergencyContactsApi from 'libs/api/facility/companyEmergencyContacts';
import { useNavigate } from 'hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { useApi } from 'hooks/useApi';
import { getShops, getInactiveShops } from 'hooks/useShops';
import EmergencyContactsAdapter from './emergencyContacts.adapter';
import Chains from 'libs/api/admin/chains';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useFilter } from 'hooks/useFilter';

const adminChainsApi = new Chains();
const api = new FacilityCompanyEmergencyContactsApi();

const statusColors = {
  new: 'blue',
  send: 'yellow',
  confirm: 'green',
  fail: 'red',
};

const useEmergencyContacts = (isMainPage = true, isEdit) => {
  const { t } = useTranslation();
  let { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const { filterData } = useFilter();
  const [chains, setChains] = useState([]);
  const [form] = Form.useForm();
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    handleDownloadExcel,
    singleItem,
    setUpdateInProcess,
    onDelete,
  } = useApi({
    resourceKey: 'company_emergency_contacts',
    singleResourceKey: 'company_emergency_contact',
    api: api,
    isMainPage,
    adapter: EmergencyContactsAdapter,
  });

  const statusTypes = useMemo(
    () => ({
      new: t('Nuovo'),
      send: t('Presa in carico'),
      confirm: t('Comfermato'),
      fail: t('Fallire'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Nome'),
        dataIndex: 'name',
        sorter: () => {},
      },
      {
        title: t('Cognome'),
        dataIndex: 'surname',
        sorter: () => {},
      },

      {
        title: t('Cellulare'),
        dataIndex: 'phone',
        sorter: () => {},
        render: (data) => (
          <p>
            <b>+39 </b>
            {data}
          </p>
        ),
      },
      {
        title: t('Ruolo'),
        dataIndex: 'role',
        sorter: () => {},
      },
      {
        title: t('Priorità di contatto'),
        dataIndex: 'priority',
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTypes[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <Link to={`${pathname}/${row.id}`}>
                <Button icon={<EyeOutlined />} />
              </Link>
            </Col>
            {row.can_update !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, statusTypes]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_phone',
        isActiveSearch: true,
      },

      {
        title: t('Catena'),
        type: 'select',
        name: 'by_chain',
        isSearch: true,
        values: chains.map((i) => ({ value: i.id, title: i.title })),
      },
    ],
    [t, filterData, chains],
  );

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRowIds(selectedRowKeys);
    },
  };

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { chains = [] },
        } = await adminChainsApi.list({
          params: {
            per: 10000,
          },
        });
        setChains(chains);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    const data = {
      company_emergency_contact: {
        ...values,
      },
    };
    try {
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'company': {
                  fieldsValues.push({
                    name: 'company_id',
                    value: singleItem[key].id,
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  const handleSendMessage = async (message) => {
    setUpdateInProcess(true);
    try {
      await api.sendMessage(selectedRowIds, message);
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      UIHelper.serverError();
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    rowSelection,
    filters,
    handleDownloadExcel,
    loading,
    onChangeTableFilter,
    shops,
    inactiveShops,
    form,
    updateInProcess,
    handleCreateOrUpdate,
    onDelete,
    singleItem,
    selectedRowIds,
    handleSendMessage,
  };
};

export default useEmergencyContacts;
